/**
 * Arena Page - Challenges Listing
 * Previously the app index; moved to /app/arena so /app/ can default-redirect
 * to /app/worldcup. The Arena tab in the navbar still surfaces this screen.
 */

import {
  useMemo, useEffect, useRef, useCallback,
} from 'react';
import { View } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { ChallengesPageProvider } from '../../src/challenges/contexts/ChallengesPageContext';
import { PageHeader, ErrorBoundary } from '../../components/layout';
import { HEADER_FLAVORS } from '../../components/layout/constants/pageHeader.constants';
import { getChallengesPageStyles } from '../../src/challenges/styles/challenges.styles';
import { useTheme } from '../../src/theme/useTheme';
import { ChallengesList } from '../../components/challenges/ChallengesList';
import { ArenaViewToggle } from '../../components/challenges/ArenaViewToggle';
import { OpenBoardList } from '../../components/challenges/OpenBoardList';
import { useGuestMonkeyArenaLoginPrompt } from '../../src/shared/hooks/useGuestMonkeyArenaLoginPrompt';
import { useGuestWalkthroughStore } from '../../src/shared/stores/guestWalkthroughStore';
import { WALKTHROUGH_STEPS } from '../../src/create/constants/guestWalkthrough.constants';
import type { WalkthroughStepKey } from '../../src/create/types/guestWalkthrough.types';
import { useSupabaseAuth } from '../../src/shared/contexts/SupabaseAuthContext';
import {
  hasSeenCreateWalkthrough,
  markCreateWalkthroughSeen,
} from '../../src/tutorial/helpers/walkthroughSeenStorage.helper';
import { useHasExistingChallenges } from '../../src/challenges/hooks/useHasExistingChallenges';
import { useAnalytics } from '../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../src/analytics';
import { useArenaViewParam } from '../../src/challenges/hooks/useArenaViewParam';

function ChallengesContent() {
  const theme = useTheme();
  const { track } = useAnalytics();
  const styles = useMemo(() => getChallengesPageStyles(theme), [theme]);
  // Home's "Find a rival" deep-links straight onto the board, on every visit (specs/home-page AC-29).
  const [viewMode, setViewMode] = useArenaViewParam();
  useGuestMonkeyArenaLoginPrompt();

  useEffect(() => {
    track(ANALYTICS_EVENTS.CHALLENGE_LIST_VIEWED);
  }, [track]);

  const router = useRouter();
  const currentStep = useGuestWalkthroughStore((s) => s.currentStep);
  const prevStepRef = useRef<WalkthroughStepKey | null>(null);
  const { user, isSignedIn, isAnonymous, isLoaded } = useSupabaseAuth();
  const { isResolved: challengesResolved, hasChallenges } = useHasExistingChallenges();
  const arenaWalkthroughCheckedRef = useRef(false);
  // Until auth resolves, assume guest: briefly hiding the toggle from a real user is recoverable,
  // flashing it at a guest who then gets refused is not.
  const canUseOpenBoard = isLoaded === true && isAnonymous !== true;

  // First Arena visit by a real user fires the create walkthrough — but only
  // if no walkthrough is already running or queued (e.g. the bridge snackbar's
  // "Show me" action already set pendingWalkthroughStart). Without this guard
  // we'd double-fire: bridge starts walkthrough → useFocusEffect clears the
  // pending flag → this effect re-sets it → next Arena visit fires it again.
  useEffect(() => {
    if (arenaWalkthroughCheckedRef.current) return;
    if (!isLoaded || !isSignedIn || isAnonymous || !user?.id) return;
    // Defer until we know whether this account already has challenges. On
    // error/timeout the probe resolves as "has challenges" so we suppress rather
    // than risk re-showing the intro to an established user.
    if (!challengesResolved) return;
    arenaWalkthroughCheckedRef.current = true;
    const userId = user.id;
    const { isActive, pendingWalkthroughStart } = useGuestWalkthroughStore.getState();
    if (isActive || pendingWalkthroughStart) return;
    void hasSeenCreateWalkthrough(userId).then(async (seen) => {
      if (seen) return;
      // Established users (≥1 existing challenge) never get the intro walkthrough.
      // Backfill the seen flag so pre-existing accounts — for whom this flag was
      // never written when the trigger shipped — are treated as already onboarded.
      if (hasChallenges) {
        await markCreateWalkthroughSeen(userId);
        return;
      }
      // Re-check just before flipping the flag — a focused bridge action could
      // have queued the walkthrough between the first check and this point.
      const current = useGuestWalkthroughStore.getState();
      if (current.isActive || current.pendingWalkthroughStart) return;
      await markCreateWalkthroughSeen(userId);
      useGuestWalkthroughStore.getState().setPendingWalkthroughStart(true);
    });
  }, [isLoaded, isSignedIn, isAnonymous, user?.id, challengesResolved, hasChallenges]);

  useFocusEffect(
    useCallback(() => {
      const { pendingWalkthroughStart, startWalkthrough } = useGuestWalkthroughStore.getState();
      if (pendingWalkthroughStart) {
        startWalkthrough();
      }
    }, [])
  );

  useEffect(() => {
    if (prevStepRef.current === WALKTHROUGH_STEPS.ARENA && currentStep === WALKTHROUGH_STEPS.DASHBOARD) {
      router.replace('/app/friends' as `/${string}`);
    }
    prevStepRef.current = currentStep;
  }, [currentStep, router]);

  return (
    <View
      style={styles.container}
      testID="challenges-page"
    >
      <PageHeader flavor={HEADER_FLAVORS.CHALLENGES} props={{}} />
      {/* arenaBody carries the web header clearance for the toggle *and* the list — see its style. */}
      <View style={styles.arenaBody}>
        {/* Guests may only play the Monkey (specs/open-challenges Q-3), and the server refuses a
            guest take-on outright — so don't offer the board rather than let them bounce off it. */}
        {canUseOpenBoard && <ArenaViewToggle value={viewMode} onChange={setViewMode} />}
        {canUseOpenBoard && viewMode === 'open' ? <OpenBoardList /> : <ChallengesList />}
      </View>
    </View>
  );
}

export default function Arena() {
  return (
    <ChallengesPageProvider>
      <ErrorBoundary>
        <ChallengesContent />
      </ErrorBoundary>
    </ChallengesPageProvider>
  );
}
